import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import {
  Grid3x3,
  Users,
  UserCircle,
  MessageCircle,
  Calendar,
  Lock,
  ChevronDown,
  CheckSquare,
  DollarSign,
  List,
  FileText,
  Clock,
  Zap,
  Map,
  MapPin,
} from "lucide-react";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { useMensagens } from "../context/MensagensContext";
import { useCandidatoContext } from "../context/CandidatoContext";

type SubNavItem = {
  name: string;
  path: string;
  roles?: string[];
};

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  roles?: string[];
  subItems?: SubNavItem[];
};

type NavGroup = {
  section: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    section: "Menu",
    items: [
      { icon: <Grid3x3 />, name: "Dashboard", path: "/dashboard" },
      {
        icon: <Users />,
        name: "Cadastros",
        subItems: [
          { name: "Candidatos", path: "/candidatos", roles: ["admin"] },
          { name: "Lideranças", path: "/liderancas" },
          { name: "Prospecção", path: "/prospeccoes" },
          { name: "Assessores", path: "/assessores", roles: ["admin", "candidato", "assessor"] },
          { name: "Comunidade", path: "/comunidade" },
          { name: "Afiliados/Apoiadores", path: "/afiliados", roles: ["admin", "candidato", "assessor"] },
          { name: "Diretórios", path: "/diretorios" },
        ],
      },
      {
        icon: <MessageCircle />,
        name: "Mensagens",
        path: "/mensagens",
        roles: ["candidato", "lideranca", "assessor"],
      },
      {
        icon: <Map />,
        name: "Mapa de Resultados",
        path: "/mapa-resultados",
        roles: ["admin", "candidato"],
      },
      {
        icon: <UserCircle />,
        name: "Usuários",
        path: "/usuarios",
        roles: ["admin"],
      },
      {
        icon: <Lock />,
        name: "Admin",
        path: "/admin",
        roles: ["admin"],
      },
      {
        icon: <FileText />,
        name: "Regras de Negócio",
        path: "/regras-negocio",
        roles: ["admin"],
      },
      {
        icon: <Zap />,
        name: "Como Funciona",
        path: "/como-funciona",
        roles: ["admin", "candidato", "assessor"],
      },
      {
        icon: <Clock />,
        name: "Acessos",
        path: "/acessos",
        roles: ["admin"],
      },
    ],
  },
  {
    section: "Gestão",
    items: [
      {
        icon: <CheckSquare />,
        name: "Tarefas",
        path: "/tarefas",
        roles: ["admin", "candidato", "lideranca", "assessor"],
      },
      {
        icon: <Calendar />,
        name: "Agenda e Eventos",
        path: "/eventos",
        roles: ["admin", "candidato", "lideranca", "assessor"],
      },
      {
        icon: <MapPin />,
        name: "Geolocalização",
        path: "/geolocalizacao",
        roles: ["admin", "candidato", "lideranca", "assessor"],
      },
      {
        icon: <DollarSign />,
        name: "Investimentos / Gastos",
        path: "/investimentos",
        roles: ["admin", "candidato", "assessor"],
      },
      {
        icon: <List />,
        name: "Solicitações",
        path: "/solicitacoes",
        roles: ["admin", "candidato", "lideranca", "assessor"],
      },
    ],
  },
];

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
      fill="currentColor"
    />
  </svg>
);

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const { user } = useAuth();
  const { nomeApp, logoPath } = useConfig();
  const { contagemNaoLidas } = useMensagens();
  const location = useLocation();
  const { selecionado } = useCandidatoContext();

  // Se admin selecionou um candidato específico, usa role "candidato"
  const roleEfetivo = user?.role === "admin" && selecionado ? "candidato" : user?.role;

  const podeVer = useCallback(
    (roles?: string[]) => !roles || roles.includes(roleEfetivo ?? ""),
    [roleEfetivo]
  );

  const gruposVisiveis = navGroups
    .map((grupo) => ({
      ...grupo,
      items: grupo.items
        .map((item) => (item.subItems ? { ...item, subItems: item.subItems.filter((s) => podeVer(s.roles)) } : item))
        .filter((item) => (item.subItems ? item.subItems.length > 0 : podeVer(item.roles))),
    }))
    .filter((grupo) => grupo.items.length > 0);

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [buscaMenu, setBuscaMenu] = useState("");

  useEffect(() => {
    const aberto = gruposVisiveis.flatMap((g) => g.items).find((item) => item.subItems?.some((s) => isActive(s.path)));
    if (aberto) setOpenSubmenu(aberto.name);

    const grupoAtivo = gruposVisiveis.find((g) =>
      g.items.some((item) => (item.path && isActive(item.path)) || item.subItems?.some((s) => isActive(s.path)))
    );
    if (grupoAtivo) setOpenGroups((prev) => ({ ...prev, [grupoAtivo.section]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSubmenu = (name: string) => {
    setOpenSubmenu((prev) => (prev === name ? null : name));
  };

  const toggleGrupo = (secao: string) => {
    setOpenGroups((prev) => ({ ...prev, [secao]: !prev[secao] }));
  };

  const showLabels = isExpanded || isHovered || isMobileOpen;

  // Busca filtra por nome (item e subitem) — grupo some se nada bate; item com
  // subItems some se nem ele nem nenhum filho bate. Sem busca, mostra tudo.
  const termoBusca = buscaMenu.trim().toLowerCase();
  const gruposFiltrados = !termoBusca
    ? gruposVisiveis
    : gruposVisiveis
        .map((grupo) => ({
          ...grupo,
          items: grupo.items
            .map((item) => {
              const nomeBate = item.name.toLowerCase().includes(termoBusca);
              if (!item.subItems) return nomeBate ? item : null;
              const subBatem = item.subItems.filter((s) => s.name.toLowerCase().includes(termoBusca));
              if (!nomeBate && subBatem.length === 0) return null;
              return { ...item, subItems: nomeBate ? item.subItems : subBatem };
            })
            .filter((item): item is NavItem => item !== null),
        }))
        .filter((grupo) => grupo.items.length > 0);

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200
        ${isExpanded || isMobileOpen ? "w-[290px]" : isHovered ? "w-[290px]" : "w-[90px]"}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`flex items-center gap-2.5 py-6 ${!showLabels ? "lg:justify-center" : ""}`}>
        <Link to="/dashboard" className="flex min-w-0 items-center gap-2.5">
          {logoPath ? (
            <img src={logoPath} alt={nomeApp} className="h-9 w-9 shrink-0 rounded-lg object-cover" />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-sm font-bold text-white">
              {nomeApp.slice(0, 1)}
            </span>
          )}
          {showLabels && (
            <span className="truncate text-base font-bold text-gray-800 dark:text-white/90">{nomeApp}</span>
          )}
        </Link>
      </div>

      {user && showLabels && user.candidatoNome && (
        <div className="mb-4">
          <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
            Campanha de <span className="text-gray-600 dark:text-gray-300">{user.candidatoNome}</span>
          </div>
        </div>
      )}

      {showLabels && (
        <div className="relative mb-5">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={buscaMenu}
            onChange={(e) => setBuscaMenu(e.target.value)}
            placeholder="Buscar no menu..."
            className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-500 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-white/5 dark:text-white/90 dark:placeholder:text-white/30"
          />
        </div>
      )}

      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-2">
            {gruposFiltrados.map((grupo) => {
              const grupoAberto = !!termoBusca || !!openGroups[grupo.section];
              return (
                <div key={grupo.section}>
                  {showLabels ? (
                    <button
                      type="button"
                      onClick={() => toggleGrupo(grupo.section)}
                      className="mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-100 dark:text-white/90 dark:hover:bg-white/5"
                    >
                      {grupo.section}
                      <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform duration-200 ${grupoAberto ? "rotate-180" : ""}`}
                      />
                    </button>
                  ) : (
                    <div className="mb-4 h-px" />
                  )}
                  {(grupoAberto || !showLabels) && (
                    <ul className="flex flex-col gap-1">
                      {grupo.items.map((nav) => (
                        <li key={nav.name}>
                          {nav.subItems ? (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleSubmenu(nav.name)}
                                className={`menu-item group w-full cursor-pointer ${
                                  openSubmenu === nav.name || termoBusca ? "menu-item-active" : "menu-item-inactive"
                                }`}
                              >
                                <span
                                  className={`menu-item-icon-size ${
                                    openSubmenu === nav.name ? "menu-item-icon-active" : "menu-item-icon-inactive"
                                  }`}
                                >
                                  {nav.icon}
                                </span>
                                {showLabels && <span className="menu-item-text">{nav.name}</span>}
                                {showLabels && (
                                  <ChevronDown
                                    size={20}
                                    className={`ml-auto transition-transform duration-200 menu-item-arrow ${
                                      openSubmenu === nav.name || termoBusca
                                        ? "menu-item-arrow-active"
                                        : "menu-item-arrow-inactive"
                                    }`}
                                  />
                                )}
                              </button>
                              {showLabels && (
                                <div
                                  className={`overflow-hidden transition-all duration-300 ${
                                    openSubmenu === nav.name || termoBusca ? "max-h-96" : "max-h-0"
                                  }`}
                                >
                                  <ul className="mt-1 ml-9 flex flex-col gap-1">
                                    {nav.subItems.map((sub) => (
                                      <li key={sub.path}>
                                        <Link
                                          to={sub.path}
                                          className={`menu-dropdown-item ${
                                            isActive(sub.path) ? "menu-dropdown-item-active" : "menu-dropdown-item-inactive"
                                          }`}
                                        >
                                          {sub.name}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          ) : (
                            <Link
                              to={nav.path!}
                              className={`menu-item group ${
                                isActive(nav.path!) ? "menu-item-active" : "menu-item-inactive"
                              }`}
                            >
                              <span className="relative">
                                <span
                                  className={`menu-item-icon-size ${
                                    isActive(nav.path!) ? "menu-item-icon-active" : "menu-item-icon-inactive"
                                  }`}
                                >
                                  {nav.icon}
                                </span>
                                {nav.path === "/mensagens" && contagemNaoLidas > 0 && !showLabels && (
                                  <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-error-500" />
                                )}
                              </span>
                              {showLabels && <span className="menu-item-text">{nav.name}</span>}
                              {showLabels && nav.path === "/mensagens" && contagemNaoLidas > 0 && (
                                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-error-500 px-1.5 text-xs font-medium text-white">
                                  {contagemNaoLidas}
                                </span>
                              )}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            {termoBusca && gruposFiltrados.length === 0 && (
              <p className="px-3 text-sm text-gray-500 dark:text-gray-400">Nada encontrado.</p>
            )}
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
